/**
 * controllerAnomalies.ts — Real-time controller-bug detector
 *
 * BETA 18.17 — Per Dylan: "have you set up anything where you can see all
 * insane and unusual bugs and such during every game played. especialy
 * games played on controller or touch screen only? ... im looking for ones
 * based on controller issues. like bugs. things that shouldnt happen when
 * keys are pressed and such. like how will you see if i click something
 * and then i cant move my character. you should be geting some kind of
 * error log and what it is in real time."
 *
 * This module watches the controller pipeline and fires "anomaly" events
 * when patterns that indicate a likely bug are observed. Each anomaly is:
 *   - Logged to console with [CTRL-ANOMALY] prefix
 *   - Dispatched as a CustomEvent('controller-anomaly') for dev panels
 *   - Stored in a ring buffer (last 100) accessible via
 *     window.__getControllerAnomalies()
 *   - Pushed to a global so livePlayTracker can attach it to the Supabase
 *     run row on death/win
 *
 * The detectors look for these specific patterns observed in the 18.x
 * controller bug investigation:
 *
 *   PHANTOM_A_BUTTON
 *     A button pressed while a choice screen / pause is active, but
 *     state.phase did NOT change within 1500 ms and the focused target
 *     did NOT change. Suggests A's onActivate didn't fire (the kind of
 *     bug 18.11 fixed where _pickLevelUp wasn't transitioning phase).
 *
 *   DPAD_NO_FOCUS_CHANGE
 *     D-pad direction button pressed while a focus screen is active, but
 *     _focusedId didn't change within 500 ms. Suggests the focus screen
 *     wasn't getting the input (the 18.10 "hover-sync below early-return"
 *     bug pattern).
 *
 *   ACTIVE_SCREEN_LINGERING
 *     state.phase transitioned to "playing" but activeScreen remained
 *     non-null for >2 seconds afterward. Suggests a missed cleanup (the
 *     18.14 "can't move after chest pick" bug pattern).
 *
 *   PHASE_STUCK_WITH_INPUT
 *     state.phase has been a non-playing phase for >30s AND gamepad
 *     activity continues to fire. Suggests the user is trying to interact
 *     with a stuck UI.
 *
 *   PAUSE_INPUT_NO_NAV
 *     Player is in pause phase, D-pad pressed, but pause menu's
 *     selectedIndex didn't change within 500 ms. Suggests the canvas
 *     pause-menu nav isn't wired (the 18.16 bug pattern).
 *
 *   XP_STUCK (BETA 18.19)
 *     Player has killed 3+ enemies and is in playing phase, but
 *     player.level + player.xp haven't budged in 15+ seconds. Catches
 *     the BETA 18.17 Killa-stuck-at-lvl-1 pattern (13 zombies killed,
 *     0 level-ups — XP not flowing from orb pickup into the player
 *     counter). When this fires, look at: lootOrb spawn site, magnet
 *     range, _queuedXp drain condition.
 */

export interface ControllerAnomaly {
  ts: number;                 // performance.now()
  category: string;           // e.g. 'PHANTOM_A_BUTTON'
  description: string;        // human-readable summary
  context: Record<string, unknown>; // free-form per-category context blob
}

const _anomalies: ControllerAnomaly[] = [];
const MAX_ANOMALIES = 100;

function _fire(category: string, description: string, context: Record<string, unknown> = {}): void {
  const a: ControllerAnomaly = { ts: performance.now(), category, description, context };
  _anomalies.push(a);
  if (_anomalies.length > MAX_ANOMALIES) _anomalies.shift();
  try { console.warn(`[CTRL-ANOMALY] ${category}: ${description}`, context); } catch { /* */ }
  try {
    if (typeof window !== 'undefined') {
      window.dispatchEvent(new CustomEvent('controller-anomaly', { detail: a }));
    }
  } catch { /* */ }
}

export function getControllerAnomalies(): ControllerAnomaly[] { return _anomalies.slice(); }
export function clearControllerAnomalies(): void { _anomalies.length = 0; }

// ─── Per-frame tracker state ────────────────────────────────────────────

interface DetectorState {
  // Last time A button was just-pressed (rising edge)
  lastAPressTs: number;
  // Snapshot of state.phase + focusedId at the moment of A press
  aPressPhase: string | null;
  aPressFocusedId: string | null;
  aPressActiveScreen: string | null;
  aPressFired: boolean;       // anomaly already fired for this press?

  // Last time a D-pad direction button was just-pressed
  lastDpadPressTs: number;
  dpadPressFocusedId: string | null;
  dpadPressActiveScreen: string | null;
  dpadPressFired: boolean;

  // Pause-menu selectedIndex tracking (for PAUSE_INPUT_NO_NAV)
  lastPauseDpadTs: number;
  pauseDpadPressIndex: number | null;
  pauseDpadFired: boolean;

  // Phase-transition tracking (for ACTIVE_SCREEN_LINGERING)
  lastPhaseChangeTs: number;
  prevPhase: string;
  activeScreenAtPhaseChange: string | null;
  phaseChangeLingerFired: boolean;

  // Stuck-phase tracking (for PHASE_STUCK_WITH_INPUT)
  phaseEnteredTs: number;
  stuckPhaseFired: boolean;

  // BETA 18.19 — XP_STUCK tracking. Captures the player's killCount + xp
  // snapshot on entry to playing phase. If killCount climbs by 3+ while
  // xp + level both stay flat for 15+ seconds, fire the anomaly.
  xpLastSnapshotTs: number;
  xpSnapshotKills: number;
  xpSnapshotXp: number;
  xpSnapshotLevel: number;
  xpStuckFired: boolean;
}

const _det: DetectorState = {
  lastAPressTs: 0, aPressPhase: null, aPressFocusedId: null, aPressActiveScreen: null, aPressFired: false,
  lastDpadPressTs: 0, dpadPressFocusedId: null, dpadPressActiveScreen: null, dpadPressFired: false,
  lastPauseDpadTs: 0, pauseDpadPressIndex: null, pauseDpadFired: false,
  lastPhaseChangeTs: 0, prevPhase: '', activeScreenAtPhaseChange: null, phaseChangeLingerFired: false,
  phaseEnteredTs: 0, stuckPhaseFired: false,
  xpLastSnapshotTs: 0, xpSnapshotKills: 0, xpSnapshotXp: 0, xpSnapshotLevel: 0, xpStuckFired: false,
};

// ─── Public: called from gamepad pipeline ───────────────────────────────

/** Called when gamepad A button rising edge detected. */
export function noteAPress(currentPhase: string, currentFocusedId: string | null, currentActiveScreen: string | null): void {
  _det.lastAPressTs = performance.now();
  _det.aPressPhase = currentPhase;
  _det.aPressFocusedId = currentFocusedId;
  _det.aPressActiveScreen = currentActiveScreen;
  _det.aPressFired = false;
}

/** Called when D-pad direction button (12-15) rising edge detected. */
export function noteDpadPress(buttonIndex: number, currentFocusedId: string | null, currentActiveScreen: string | null, pauseSelectedIndex: number | null): void {
  _det.lastDpadPressTs = performance.now();
  _det.dpadPressFocusedId = currentFocusedId;
  _det.dpadPressActiveScreen = currentActiveScreen;
  _det.dpadPressFired = false;
  // Separate pause-menu D-pad tracking
  if (pauseSelectedIndex !== null) {
    _det.lastPauseDpadTs = performance.now();
    _det.pauseDpadPressIndex = pauseSelectedIndex;
    _det.pauseDpadFired = false;
  }
  // Suppress unused-var lint for the index — kept for future per-direction analytics.
  void buttonIndex;
}

// ─── Per-frame detector tick ────────────────────────────────────────────

/**
 * Run the anomaly detectors. Call once per frame from updateGame.
 * Reads current pipeline state and compares against the snapshots
 * captured at the moment of input.
 */
export function tickControllerAnomalies(args: {
  currentPhase: string;
  currentFocusedId: string | null;
  currentActiveScreen: string | null;
  pauseSelectedIndex: number | null;
  controllerConnected: boolean;
  gamepadActivityRecentMs: number; // ms since last gamepad input
  // BETA 18.19 — XP_STUCK inputs. Snapshot the player's run progress so
  // the detector can spot "kills go up but XP/level don't" patterns.
  playerLevel?: number;
  playerXp?: number;
  killCount?: number;
}): void {
  const now = performance.now();

  // ─── PHANTOM_A_BUTTON ─────────────────────────────────────────────
  // A pressed >1500ms ago, on a screen, no phase change AND no focus change.
  if (
    !_det.aPressFired &&
    _det.lastAPressTs > 0 &&
    now - _det.lastAPressTs > 1500 &&
    _det.aPressActiveScreen !== null &&
    args.currentPhase === _det.aPressPhase &&
    args.currentFocusedId === _det.aPressFocusedId &&
    args.currentActiveScreen === _det.aPressActiveScreen
  ) {
    _det.aPressFired = true;
    _fire(
      'PHANTOM_A_BUTTON',
      `A pressed on ${_det.aPressActiveScreen} but nothing changed in 1500ms`,
      {
        screen: _det.aPressActiveScreen,
        focusedId: _det.aPressFocusedId,
        phase: _det.aPressPhase,
      },
    );
  }

  // ─── DPAD_NO_FOCUS_CHANGE ─────────────────────────────────────────
  // D-pad pressed >500ms ago, on a focus screen, focusedId unchanged.
  if (
    !_det.dpadPressFired &&
    _det.lastDpadPressTs > 0 &&
    now - _det.lastDpadPressTs > 500 &&
    _det.dpadPressActiveScreen !== null &&
    args.currentActiveScreen === _det.dpadPressActiveScreen &&
    args.currentFocusedId === _det.dpadPressFocusedId
  ) {
    _det.dpadPressFired = true;
    _fire(
      'DPAD_NO_FOCUS_CHANGE',
      `D-pad pressed on ${_det.dpadPressActiveScreen} but focus didn't advance in 500ms`,
      {
        screen: _det.dpadPressActiveScreen,
        focusedId: _det.dpadPressFocusedId,
      },
    );
  }

  // ─── PAUSE_INPUT_NO_NAV ───────────────────────────────────────────
  // D-pad pressed while paused, canvas-pause selectedIndex unchanged.
  if (
    !_det.pauseDpadFired &&
    _det.lastPauseDpadTs > 0 &&
    now - _det.lastPauseDpadTs > 500 &&
    args.currentPhase === 'paused' &&
    args.pauseSelectedIndex === _det.pauseDpadPressIndex
  ) {
    _det.pauseDpadFired = true;
    _fire(
      'PAUSE_INPUT_NO_NAV',
      `D-pad pressed in pause but selectedIndex didn't change in 500ms`,
      { selectedIndex: _det.pauseDpadPressIndex },
    );
  }

  // ─── ACTIVE_SCREEN_LINGERING ──────────────────────────────────────
  // Phase changed to "playing" but activeScreen still set 2s later.
  if (_det.prevPhase !== args.currentPhase) {
    _det.lastPhaseChangeTs = now;
    _det.prevPhase = args.currentPhase;
    _det.activeScreenAtPhaseChange = args.currentActiveScreen;
    _det.phaseChangeLingerFired = false;
    _det.phaseEnteredTs = now;
    _det.stuckPhaseFired = false;
  } else if (
    !_det.phaseChangeLingerFired &&
    args.currentPhase === 'playing' &&
    args.currentActiveScreen !== null &&
    now - _det.lastPhaseChangeTs > 2000
  ) {
    _det.phaseChangeLingerFired = true;
    _fire(
      'ACTIVE_SCREEN_LINGERING',
      `state.phase = 'playing' but activeScreen still '${args.currentActiveScreen}' after 2s`,
      { screen: args.currentActiveScreen },
    );
  }

  // ─── PHASE_STUCK_WITH_INPUT ───────────────────────────────────────
  // Non-playing phase for >30s with gamepad activity in the last 5s.
  if (
    !_det.stuckPhaseFired &&
    args.currentPhase !== 'playing' &&
    args.currentPhase !== 'death_replay' &&
    args.currentPhase !== 'loading' &&
    now - _det.phaseEnteredTs > 30000 &&
    args.controllerConnected &&
    args.gamepadActivityRecentMs < 5000
  ) {
    _det.stuckPhaseFired = true;
    _fire(
      'PHASE_STUCK_WITH_INPUT',
      `state.phase = '${args.currentPhase}' for 30s+ while gamepad active — likely stuck UI`,
      {
        phase: args.currentPhase,
        screen: args.currentActiveScreen,
        focusedId: args.currentFocusedId,
      },
    );
  }

  // ─── XP_STUCK (BETA 18.19) ────────────────────────────────────────
  // Snapshot kills + xp + level every time we re-enter playing phase.
  // After 15s in playing, if killCount has gone up by 3+ but xp + level
  // haven't moved → XP pipeline is broken somewhere. Skips when player
  // is at high level (xpToNext grows so they may legitimately stall).
  if (args.playerLevel !== undefined && args.playerXp !== undefined && args.killCount !== undefined) {
    if (args.currentPhase === 'playing') {
      if (_det.xpLastSnapshotTs === 0 || _det.xpStuckFired) {
        // Fresh snapshot on first playing tick or after firing once
        _det.xpLastSnapshotTs = now;
        _det.xpSnapshotKills = args.killCount;
        _det.xpSnapshotXp = args.playerXp;
        _det.xpSnapshotLevel = args.playerLevel;
        _det.xpStuckFired = false;
      } else if (now - _det.xpLastSnapshotTs > 15000) {
        const killDelta = args.killCount - _det.xpSnapshotKills;
        const xpDelta = args.playerXp - _det.xpSnapshotXp;
        const levelDelta = args.playerLevel - _det.xpSnapshotLevel;
        // Only fire if kills went up, no XP gain, no level change, AND
        // we're below lvl 30 (above that, slow XP progress is expected).
        if (killDelta >= 3 && xpDelta === 0 && levelDelta === 0 && args.playerLevel < 30) {
          _det.xpStuckFired = true;
          _fire(
            'XP_STUCK',
            `${killDelta} kills in 15s but XP frozen at ${args.playerXp} and level stuck at ${args.playerLevel}`,
            {
              killDelta,
              playerXp: args.playerXp,
              playerLevel: args.playerLevel,
              killCount: args.killCount,
            },
          );
        } else if (xpDelta > 0 || levelDelta > 0) {
          // XP is flowing — re-snapshot to start a fresh detection window.
          _det.xpLastSnapshotTs = now;
          _det.xpSnapshotKills = args.killCount;
          _det.xpSnapshotXp = args.playerXp;
          _det.xpSnapshotLevel = args.playerLevel;
        }
      }
    }
  }
}

// ─── window global registration ─────────────────────────────────────────

if (typeof window !== 'undefined') {
  (window as unknown as { __getControllerAnomalies?: () => ControllerAnomaly[] }).__getControllerAnomalies = getControllerAnomalies;
  (window as unknown as { __clearControllerAnomalies?: () => void }).__clearControllerAnomalies = clearControllerAnomalies;
}
